import { useState, useMemo, useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Typography,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  alpha,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { ResponsiveTable, Column } from '../../components/mobile/ResponsiveTable';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SearchIcon from '@mui/icons-material/Search';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import PersonIcon from '@mui/icons-material/Person';
import SendIcon from '@mui/icons-material/Send';
import axiosInstance from '../../lib/axios';
import { TableToolbar, FilterOption, ColumnConfig } from '../../components/TableToolbar';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { useSnackbar } from '../../components/SnackbarProvider';
import { Quote, QuoteStatus } from '../../types/b2b';

const fetchQuotes = async () => {
  const response = await axiosInstance.get('/quotes');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const filterOptions: FilterOption[] = [
  {
    field: 'status',
    label: 'Status',
    type: 'select',
    options: [
      { value: 'draft', label: 'Draft' },
      { value: 'sent', label: 'Sent' },
      { value: 'accepted', label: 'Accepted' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'expired', label: 'Expired' },
      { value: 'canceled', label: 'Canceled' },
    ],
  },
];

const exportColumns: ColumnConfig[] = [
  { field: 'quoteNumber', headerName: 'Quote #' },
  {
    field: 'customer',
    headerName: 'Customer',
    valueFormatter: (_, row) => row.customer ? `${row.customer.firstName} ${row.customer.lastName}` : 'N/A',
  },
  { field: 'status', headerName: 'Status' },
  {
    field: 'total',
    headerName: 'Total',
    valueFormatter: (value) => `${Number(value).toLocaleString()} DZD`,
  },
  {
    field: 'validUntil',
    headerName: 'Valid Until',
    valueFormatter: (value) => new Date(value).toLocaleDateString(),
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    valueFormatter: (value) => new Date(value).toLocaleDateString(),
  },
];

const getStatusColor = (status: QuoteStatus): string => {
  switch (status) {
    case 'draft':
      return palette.textMuted;
    case 'sent':
      return palette.info;
    case 'accepted':
      return palette.success;
    case 'rejected':
      return palette.error;
    case 'expired':
      return palette.warning;
    case 'canceled':
      return palette.textMuted;
    default:
      return palette.textMuted;
  }
};

export const QuoteList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mode } = useContext(ColorModeContext);
  const { showMessage } = useSnackbar();
  const animations = useAnimationStyles();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);

  const { data: quotes, isLoading } = useQuery({
    queryKey: ['quotes'],
    queryFn: fetchQuotes,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => axiosInstance.delete(`/quotes/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      showMessage('Quote deleted successfully', 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to delete quote', 'error');
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => axiosInstance.post(`/quotes/${id}/duplicate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      showMessage('Quote duplicated successfully', 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to duplicate quote', 'error');
    },
  });

  const sendMutation = useMutation({
    mutationFn: (id: string) => axiosInstance.post(`/quotes/${id}/send`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      showMessage('Quote sent successfully', 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to send quote', 'error');
    },
  });

  const filteredData = useMemo(() => {
    if (!quotes) return [];

    return quotes.filter((item: Quote) => {
      if (searchValue) {
        const search = searchValue.toLowerCase();
        const customerName = item.customer
          ? `${item.customer.firstName} ${item.customer.lastName}`
          : '';
        const matchesSearch =
          item.quoteNumber.toLowerCase().includes(search) ||
          customerName.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }

      if (activeFilters.status && item.status !== activeFilters.status) {
        return false;
      }

      return true;
    });
  }, [quotes, searchValue, activeFilters]);

  const paginatedData = useMemo(() => {
    const startIndex = page * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, page, pageSize]);

  const handleFilterChange = (field: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setActiveFilters({});
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedQuote(null);
  };

  const handleDelete = () => {
    if (selectedQuote && window.confirm('Are you sure you want to delete this quote?')) {
      deleteMutation.mutate(selectedQuote.id);
    }
    handleMenuClose();
  };

  const handleDuplicate = () => {
    if (selectedQuote) {
      duplicateMutation.mutate(selectedQuote.id);
    }
    handleMenuClose();
  };

  const handleSend = () => {
    if (selectedQuote) {
      sendMutation.mutate(selectedQuote.id);
    }
    handleMenuClose();
  };

  const handleDownloadPdf = async () => {
    if (selectedQuote) {
      try {
        const response = await axiosInstance.get(`/quotes/${selectedQuote.id}/pdf`, {
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = `quote-${selectedQuote.quoteNumber}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        showMessage('PDF downloaded successfully', 'success');
      } catch {
        showMessage('Failed to download PDF', 'error');
      }
    }
    handleMenuClose();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-DZ', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const stats = useMemo(() => {
    const total = filteredData.length;
    const draft = filteredData.filter((q: Quote) => q.status === 'draft').length;
    const sent = filteredData.filter((q: Quote) => q.status === 'sent').length;
    const accepted = filteredData.filter((q: Quote) => q.status === 'accepted').length;
    const totalValue = filteredData.reduce((sum: number, q: Quote) => sum + (q.total || 0), 0);

    return { total, draft, sent, accepted, totalValue };
  }, [filteredData]);

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const getStatusColorType = (status: QuoteStatus): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'accepted': return 'success';
      case 'rejected': return 'error';
      case 'expired': return 'warning';
      case 'sent': return 'info';
      default: return 'default';
    }
  };

  // Column definitions for ResponsiveTable
  const tableColumns: Column<Quote>[] = [
    {
      field: 'quoteNumber',
      headerName: 'Quote #',
      render: (value) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RequestQuoteIcon sx={{ fontSize: 18, color: palette.primary }} />
          <Typography variant="body2" sx={{ fontFamily: typography.fontFamily.mono, color: colors.textPrimary, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold }}>
            {value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'customer',
      headerName: 'Customer',
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon sx={{ fontSize: 18, color: palette.info }} />
          <Typography variant="body2" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.semibold, color: colors.textPrimary, fontSize: typography.fontSize.sm }}>
            {row.customer ? `${row.customer.firstName} ${row.customer.lastName}` : 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      render: (value) => {
        const statusColor = getStatusColor(value);
        return (
          <Chip label={value.toUpperCase()} sx={{ backgroundColor: alpha(statusColor, 0.1), color: statusColor, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, border: `1px solid ${statusColor}` }} />
        );
      },
    },
    {
      field: 'total',
      headerName: 'Total',
      render: (value) => (
        <Box sx={{ display: 'inline-flex', px: 2, py: 0.75, borderRadius: theme.radius.xs, backgroundColor: alpha(palette.success, 0.1), border: `1px solid ${palette.success}` }}>
          <Typography sx={{ fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.extrabold, color: palette.success }}>
            {formatCurrency(value || 0)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'validUntil',
      headerName: 'Valid Until',
      render: (value) => (
        <Typography variant="body2" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.semibold, color: colors.textPrimary, fontSize: typography.fontSize.sm }}>
          {formatDate(value)}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      render: (value) => (
        <Typography variant="body2" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.semibold, color: colors.textPrimary, fontSize: typography.fontSize.sm }}>
          {formatDate(value)}
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Header */}
      <Box sx={{ mb: 4, ...animations.slideDown }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <RequestQuoteIcon sx={{ color: palette.primary, fontSize: 24 }} />
              <Typography
                variant="overline"
                sx={{
                  color: palette.primary,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                B2B SUPPLIER SYSTEM
              </Typography>
            </Box>
            <Typography
              variant="h3"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                color: colors.textPrimary,
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
              }}
            >
              QUOTES
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/app/quotes/create')}
            sx={{
              backgroundColor: palette.primary,
              color: '#FFFFFF',
              fontWeight: typography.fontWeight.bold,
              textTransform: 'uppercase',
              fontSize: typography.fontSize.sm,
              px: 3,
              py: 1.5,
              borderRadius: theme.radius.sm,
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&:hover': {
                backgroundColor: alpha(palette.primary, 0.8),
                transform: 'translateY(-2px) scale(1.02)',
                boxShadow: `0 8px 24px ${alpha(palette.primary, 0.3)}`,
              },
            }}
          >
            New Quote
          </Button>
        </Box>
      </Box>

      {/* Stats Dashboard */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 3, ...animations.slideUp(0.1) }}>
        <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 2.5, position: 'relative', overflow: 'hidden', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '4px', backgroundColor: palette.primary } }}>
          <Typography variant="overline" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, letterSpacing: typography.letterSpacing.widest, fontWeight: typography.fontWeight.bold }}>TOTAL QUOTES</Typography>
          <Typography variant="h3" sx={{ fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.extrabold, color: colors.textPrimary, lineHeight: 1, mt: 1 }}>{stats.total}</Typography>
        </Card>

        <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 2.5, position: 'relative', overflow: 'hidden', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '4px', backgroundColor: palette.textMuted } }}>
          <Typography variant="overline" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, letterSpacing: typography.letterSpacing.widest, fontWeight: typography.fontWeight.bold }}>DRAFT</Typography>
          <Typography variant="h3" sx={{ fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.extrabold, color: colors.textPrimary, lineHeight: 1, mt: 1 }}>{stats.draft}</Typography>
        </Card>

        <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 2.5, position: 'relative', overflow: 'hidden', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '4px', backgroundColor: palette.info } }}>
          <Typography variant="overline" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, letterSpacing: typography.letterSpacing.widest, fontWeight: typography.fontWeight.bold }}>SENT</Typography>
          <Typography variant="h3" sx={{ fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.extrabold, color: palette.info, lineHeight: 1, mt: 1 }}>{stats.sent}</Typography>
        </Card>

        <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 2.5, position: 'relative', overflow: 'hidden', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '4px', backgroundColor: palette.success } }}>
          <Typography variant="overline" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, letterSpacing: typography.letterSpacing.widest, fontWeight: typography.fontWeight.bold }}>ACCEPTED</Typography>
          <Typography variant="h3" sx={{ fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.extrabold, color: palette.success, lineHeight: 1, mt: 1 }}>{stats.accepted}</Typography>
        </Card>

        <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 2.5, position: 'relative', overflow: 'hidden', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '4px', backgroundColor: palette.success } }}>
          <Typography variant="overline" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, letterSpacing: typography.letterSpacing.widest, fontWeight: typography.fontWeight.bold }}>TOTAL VALUE</Typography>
          <Typography variant="h5" sx={{ fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.extrabold, color: palette.success, lineHeight: 1, mt: 1 }}>{formatCurrency(stats.totalValue)}</Typography>
        </Card>
      </Box>

      {/* Search & Filter Bar */}
      <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 2, mb: 3, ...animations.slideUp(0.15) }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="SEARCH QUOTES..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            sx={{
              flex: 1,
              minWidth: 300,
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors.surfaceElevated,
                border: `1px solid ${colors.border}`,
                borderRadius: theme.radius.sm,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                '& fieldset': { border: 'none' },
                '&:hover': { borderColor: colors.borderLight },
                '&.Mui-focused': { borderColor: palette.primary },
              },
              '& .MuiInputBase-input': { color: colors.textPrimary, fontWeight: typography.fontWeight.medium, textTransform: 'uppercase' },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.textMuted }} />
                </InputAdornment>
              ),
            }}
          />
          <TableToolbar
            title=""
            searchValue=""
            onSearchChange={() => {}}
            searchPlaceholder=""
            filters={filterOptions}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            data={filteredData}
            columns={exportColumns}
            exportFileName="quotes"
          />
        </Box>
      </Card>

      {/* Quotes Table */}
      <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, overflow: 'hidden', ...animations.slideUp(0.2) }}>
        <ResponsiveTable
          columns={tableColumns}
          rows={paginatedData}
          loading={isLoading}
          emptyMessage="No quotes found"
          primaryField="quoteNumber"
          getPrimaryText={(row) => row.quoteNumber || `Quote #${row.id?.slice(0, 8) || 'Unknown'}`}
          statusField="status"
          getStatusColor={(status) => getStatusColorType(status as QuoteStatus)}
          onRowClick={(quote) => navigate(`/app/quotes/show/${quote.id}`)}
        />
        <Box sx={{ backgroundColor: colors.surfaceElevated, borderTop: `2px solid ${colors.border}` }}>
          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
              '& .MuiTablePagination-toolbar': { color: colors.textPrimary, fontFamily: typography.fontFamily.mono },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wide },
            }}
          />
        </Box>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
            boxShadow: `0 8px 24px ${alpha('#000', 0.2)}`,
          },
        }}
      >
        {selectedQuote?.status === 'draft' && (
          <MenuItem onClick={handleSend}>
            <ListItemIcon><SendIcon fontSize="small" sx={{ color: palette.info }} /></ListItemIcon>
            <ListItemText primary="Send Quote" sx={{ color: colors.textPrimary }} />
          </MenuItem>
        )}
        <MenuItem onClick={handleDuplicate}>
          <ListItemIcon><ContentCopyIcon fontSize="small" sx={{ color: palette.primary }} /></ListItemIcon>
          <ListItemText primary="Duplicate" sx={{ color: colors.textPrimary }} />
        </MenuItem>
        <MenuItem onClick={handleDownloadPdf}>
          <ListItemIcon><PictureAsPdfIcon fontSize="small" sx={{ color: palette.error }} /></ListItemIcon>
          <ListItemText primary="Download PDF" sx={{ color: colors.textPrimary }} />
        </MenuItem>
        {selectedQuote?.status === 'draft' && (
          <MenuItem onClick={handleDelete}>
            <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: palette.error }} /></ListItemIcon>
            <ListItemText primary="Delete" sx={{ color: palette.error }} />
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};
